import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Icon } from '@ui-library/core'
import { Popin } from '@ui-library/core/Popin'
import { AddBold, InfoCircleLined } from '@ui-library/icons'
import { FormatStyled, IconMoreInfo, InputWrapper, PopinContainer, PopinDescription, PopinImages, PopinTitle, SpanCoverage, SpanMax, StrongBudget, TableStyled, ThBlue, TotalAndBudget } from '../common/styled'

import luxResidence from '../../../assets/images/medium/luxResidence.svg'
import bellesDemeures from '../../../assets/images/medium/bellesDemeures.svg'

import Loader from '../../Loader/Loader'

import { displayCanSaveSelector, setCanSave } from '../../../redux/reducers/Display/display.reducer'

import { ForecastResult } from '../../../utils/myAdfactoryApi/swaggerApi'
import { formatNumbers } from '../../../utils/tsUtils'
import { DISPLAY_PRODUCTS_FORMATS } from '../../../utils/variables'

type PrintByFormatProps = {
  currentMarket: string,
  formatsResult: any[],
  cpms: { [key: string]: number } | undefined,
  isProposal?: boolean,
  loading: string,
  totalBudget: number,
  totalOffer: number,
  onChange?: (data: any) => void,
}

export const defineMaximumBudget = (data: any, cpm: number = 0) => {
  if (data.available) {
    return Math.round((cpm * data.available) / 1000)
  }
  return 0
}

export const defineAvailableImp = (fr: ForecastResult) => Math.round(fr?.available ?? 0)
export const definePurchasedImp = (fr: ForecastResult) => Math.round(fr?.purchased ?? 0)
export const defineCpm = (fr: ForecastResult, cpms: { [key: string]: number } | undefined) => {
  if (fr.code && cpms && cpms[fr.code]) {
    return cpms[fr.code]
  }
  return 0
}

const InputBudget = ({ data, onChange, cpm }: { data: any, onChange: (data: any) => void, cpm: number }): React.ReactElement | null => {
  const dispatch = useDispatch()
  const canSave = useSelector(displayCanSaveSelector)
  const [budgetValue, setBudgetValue] = useState(data.budget)
  const maximumBudget: number = defineMaximumBudget(data, cpm)
  const isDisabled: boolean = maximumBudget === 0 || (data.isDisabled ?? false)

  const handleChange = (event: any) => {
    const valueWithoutSpacing = event.target.value.replace(/\D+/g, '')
    if (!Number.isNaN(valueWithoutSpacing)) {
      if (!canSave) dispatch(setCanSave(true))
      if (valueWithoutSpacing === '') {
        return setBudgetValue(0)
      }

      if (valueWithoutSpacing > maximumBudget) {
        return false
      }

      return setBudgetValue(parseInt(valueWithoutSpacing))
    }
    return false
  }

  /**
   * WHEN PURCHASED INPUT IS USED, SET BUDGET INPUT WITH NEW VALUE FROM DATA
   */
  useEffect(() => {
    setBudgetValue(data.budget)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.purchased])

  /**
   * IF BUGDET INPUT IS USED, CALCULATE THE NEW PURCHASED VALUE OF DATA AND UPDATE
   */
  useEffect(() => {
    const broadcastData = {
      ...data,
      purchased: !Number.isNaN(Math.round((budgetValue / cpm) * 1000)) ? Math.round((budgetValue / cpm) * 1000) : 0,
      budget: budgetValue,
    }
    onChange(broadcastData)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetValue])

  return (
    <div className={isDisabled ? 'isDisabled' : ''}>
      <InputWrapper>
        <input
          disabled={isDisabled}
          type="text"
          value={formatNumbers(budgetValue)}
          onChange={handleChange}
        />
        <span>€</span>
      </InputWrapper>
      <SpanMax>Max. {formatNumbers(maximumBudget)}€</SpanMax>
    </div>
  )
}

const InputPrint = ({ data, onChange, cpm }: { data: any, onChange: (data: any) => void, cpm: number }): React.ReactElement | null => {
  const dispatch = useDispatch()
  const canSave = useSelector(displayCanSaveSelector)
  const [printValue, setPrintValue] = useState(Math.min(data.purchased, data.available))
  const maximumBudget: number = defineMaximumBudget(data, cpm)
  const isDisabled: boolean = maximumBudget === 0 || (data.isDisabled ?? false)

  const handleChange = (event: any) => {
    const valueWithoutSpacing = event.target.value.replace(/\D+/g, '')
    if (!Number.isNaN(valueWithoutSpacing)) {
      if (!canSave) dispatch(setCanSave(true))
      if (valueWithoutSpacing === '') {
        return setPrintValue(0)
      }

      if (valueWithoutSpacing > data.available) {
        return false
      }

      return setPrintValue(parseInt(valueWithoutSpacing))
    }
    return false
  }

  /**
   * WHEN BUDGET INPUT IS USED, SET PUSCHASED INPUT WITH NEW VALUE FROM DATA
   */
  useEffect(() => {
    setPrintValue(Math.min(data.purchased, data.available))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.budget])

  /**
   * IF PUSCHASED INPUT IS USED, CALCULATE THE NEW BUDGET VALUE OF DATA AND UPDATE
   */
  useEffect(() => {
    const broadcastData = {
      ...data,
      purchased: printValue,
      budget: !Number.isNaN(Math.round((printValue / 1000) * cpm)) ? Math.round((printValue / 1000) * cpm) : 0,
    }
    onChange(broadcastData)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printValue])
  return (
    <div className={isDisabled ? 'isDisabled' : ''}>
      <InputWrapper>
        <input
          disabled={isDisabled}
          type="text"
          value={formatNumbers(printValue)}
          onChange={handleChange}
        />
        <span>Imp.</span>
      </InputWrapper>
      <SpanCoverage>Soit {(Math.round(((printValue ?? 0) / (data.total ?? 0)) * 100) || 0)}% de couverture</SpanCoverage>
    </div>
  )
}

const PrintsByFormatTableForLuxury = ({
  currentMarket,
  formatsResult,
  cpms,
  isProposal = false,
  loading,
  totalBudget,
  totalOffer,
  onChange = (() => true),
}: PrintByFormatProps): React.ReactElement => {
  const [isPopinOpen, setIsPopinOpen] = useState<boolean>(false)
  const [productToDisplay, setProductToDisplay] = useState('')

  const isPending = loading === 'pending'

  const displayPopinWithContent = (productCode: string | undefined) => {
    if (productCode) {
      setIsPopinOpen(true)
      setProductToDisplay(productCode)
    }
  }
  const popinContent = DISPLAY_PRODUCTS_FORMATS.Luxe[productToDisplay]?.popinContent

  return (
    <TableStyled data-testid="PrintsByFormatTableForLuxury">
      <thead>
        <tr>
          <th scope="col">
            Type de format
          </th>

          {!isProposal && <th scope="col">Déjà vendues <span>(sur la période)</span></th>}
          {!isProposal && <th scope="col">Disponibles <span>(sur la période)</span></th>}
          <ThBlue scope="col">Impressions proposées <span>(sur la période)</span></ThBlue>
          <ThBlue scope="col">Budgets <span>(hors taxe)</span></ThBlue>
        </tr>
      </thead>
      <tbody>
        {formatsResult?.length > 0 && (
          formatsResult.map((fr: ForecastResult) => {
            const frCopy = { ...fr }
            const productContent = frCopy.code && DISPLAY_PRODUCTS_FORMATS[currentMarket][frCopy.code]
            const availableImp = defineAvailableImp(frCopy)
            const purchasedImp = definePurchasedImp(frCopy)
            const cpm = defineCpm(frCopy, cpms)

            // If table is used in a proposal page, we want to hide the "zero" lines
            if (isProposal && frCopy.budget === 0) {
              return null
            }

            return (
              <tr key={`row-${frCopy.code}`}>
                <td>
                  <FormatStyled>
                    <img src={productContent?.svg} style={{ width: '7em' }} alt="masthead" />
                    <div>
                      <h1 className="productTitle">{productContent?.title}</h1>
                      <div className="productName">
                        <p>{productContent?.productName}</p>
                        <IconMoreInfo onClick={() => displayPopinWithContent(frCopy.code)}>
                          <Icon icon={InfoCircleLined} size="0.9em" />
                        </IconMoreInfo>
                      </div>
                      <p className="productDescription">{productContent?.description}</p>
                      {fr.code !== 'Natif' && (
                        <div className="retargetingFeature">
                          <Icon icon={AddBold} size="0.7em" style={{ marginTop: '0.3em' }} /> Retargeting
                        </div>
                      )}
                    </div>
                  </FormatStyled>
                </td>
                {!isProposal && (
                  <>
                    <td>
                      {isPending ? (
                        <Loader maxWidth={100} height={20} />
                      ) : (
                        <>
                          <div className="circleSold" />
                          {formatNumbers(frCopy.sold ?? 0)} Imp.
                        </>
                      )}
                    </td>
                    <td>
                      {isPending ? (
                        <>
                          <Loader maxWidth={100} height={20} />
                          <SpanCoverage><Loader maxWidth={150} height={15} /></SpanCoverage>
                        </>
                      ) : (
                        <div className="availableBlock">
                          <div className="circleAvailable" />
                          <div>
                            {formatNumbers(availableImp)} Imp.
                            {frCopy?.available && frCopy?.total ? <SpanCoverage>Soit {Math.round((frCopy.available / frCopy.total) * 100) || 0}% de couverture</SpanCoverage> : ''}
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                )}
                <td data-testid={`inputPrint-${fr.code}`}>
                  {isPending ? (
                    <>
                      <Loader maxWidth={300} height={35} style={{ marginBottom: '5px' }} />
                      <Loader maxWidth={120} height={20} />
                    </>
                  ) : (
                    <>
                      {!isProposal ? (
                        <InputPrint data={fr} onChange={onChange} cpm={cpm} />
                      ) : (
                        <StrongBudget>{formatNumbers(Math.min(purchasedImp, availableImp))} Imp.</StrongBudget>
                      )}
                    </>
                  )}
                </td>
                <td data-testid={`inputBudget-${fr.code}`}>
                  {(isPending || fr.budget === undefined) ? (
                    <>
                      <Loader maxWidth={300} height={35} style={{ marginBottom: '5px' }} />
                      <Loader maxWidth={80} height={20} />
                    </>
                  ) : (
                    <>
                      {!isProposal ? (
                        <InputBudget data={fr} onChange={onChange} cpm={cpm} />
                      ) : (
                        <StrongBudget>{fr.budget} €</StrongBudget>
                      )}
                    </>
                  )}
                </td>
              </tr>
            )
          })
        )}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={!isProposal ? 3 : 1} style={{ textAlign: 'right' }}>
            <strong>Total du comptage :</strong>
          </td>
          <td>
            {isPending ? (
              <Loader maxWidth={80} height={20} />
            ) : (
              <TotalAndBudget $hideBudget={false}>
                <strong>{formatNumbers(totalOffer)} Imp.</strong>
              </TotalAndBudget>
            )}
          </td>
          <td>
            {isPending ? (
              <Loader maxWidth={80} height={20} style={{ marginLeft: 'auto' }} />
            ) : (
              <StrongBudget>{formatNumbers(totalBudget)} €</StrongBudget>
            )}
          </td>
        </tr>
      </tfoot>
      <PopinStyled
        open={isPopinOpen}
        size="small"
        onClose={() => { setIsPopinOpen(false) }}
      >
        <PopinContainer>
          <PopinImages>
            <div className="productImage">
              <img src={bellesDemeures} style={{ width: '3em', marginBottom: '0.5em' }} alt="belleDemeures" />
              <img src={popinContent?.logos[0]} style={{ width: '11em' }} alt="imageProduct" />
            </div>
            <div className="productImage">
              <img src={luxResidence} style={{ width: '3em', marginBottom: '0.5em' }} alt="luxResidence" />
              <img src={popinContent?.logos[1]} style={{ width: '11em' }} alt="imageProduct" />
            </div>
          </PopinImages>
          <PopinTitle>{popinContent?.title}</PopinTitle>
          <PopinDescription>{popinContent?.description}</PopinDescription>
          <p>{popinContent?.notice}</p>
        </PopinContainer>
      </PopinStyled>
    </TableStyled>
  )
}

const PopinStyled = styled(Popin)`
  section {
    padding: 0 0 1.5em;
  }
`

export default PrintsByFormatTableForLuxury
